/**
 * SearchesToolbar — the My Searches header: title + count, the owner dropdown,
 * the status pills, and the debounced text search.
 *
 * The owner control was a two-option Mine/Everyone toggle while there was
 * nowhere in the UI to get a colleague's Thrive user id from. `GET
 * /searches-list/leads` supplies them now, so it is a dropdown: `Mine` /
 * `Everyone` / one entry per lead with the number of searches picking them
 * returns.
 *
 * A NATIVE `<select>` rather than the imports list's Radix menu. The option
 * list is unbounded (one per lead, dozens on real data), and a native select
 * gets type-ahead, scrolling and the platform's own long-list handling for
 * free — none of which the menu has. `<optgroup label="Leads">` carries the
 * separator the mockup drew as `────`, with semantics a screen reader can use.
 *
 * Design-system token utilities only, English copy only.
 */
import type { SearchLeadOption, SearchStatusFilter } from "@nu-assist/contracts";
import { Search } from "lucide-react";

export interface SearchesToolbarProps {
  /**
   * Matching searches, or `null` when no request under the filters currently in
   * this toolbar has answered yet.
   *
   * `null` rather than `0`, because the count is a factual claim about those
   * filters: rendering "My Searches (0)" while the request is still in flight
   * states that the filters matched nothing, which is the same unanswered claim
   * the table's own loading state exists to avoid.
   */
  total: number | null;
  /** Raw (un-debounced) search text the input shows. */
  searchText: string;
  onSearchTextChange: (text: string) => void;
  owner: string;
  onOwnerChange: (owner: string) => void;
  /**
   * Leads to offer beneath `Mine` / `Everyone`, each with the number of
   * searches selecting it returns. Empty while the request is in flight, and
   * empty if it FAILED — the two reserved options always work, so a dropdown
   * that lost its colleague list is degraded rather than broken.
   */
  leads: readonly SearchLeadOption[];
  status: SearchStatusFilter;
  onStatusChange: (status: SearchStatusFilter) => void;
}

/** Status pills, in wire order. `open_bd` first — it is the default view.
 *  Exported because `SearchesPage` derives its `?status=` allow-list from it:
 *  "has a pill" and "is URL-addressable" must stay one fact, not two lists
 *  that agree by hand (PR #674 review iter 2, A§2a). */
export const STATUS_PILLS: ReadonlyArray<{ value: SearchStatusFilter; label: string }> = [
  { value: "open_bd", label: "Open + BD" },
  { value: "open", label: "Open" },
  { value: "bd", label: "Business Development" },
  { value: "all", label: "All" },
];

/** The two reserved scopes, always present and always first. */
const OWNER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "me", label: "Mine" },
  { value: "all", label: "Everyone" },
];

/** `Meredith Blake (567)`, or the email for a lead with no federation row. */
function leadLabel(lead: SearchLeadOption): string {
  const who = lead.name ?? lead.email ?? `Thrive user ${lead.thrive_user_id}`;
  return `${who} (${lead.search_count.toLocaleString()})`;
}

const ACTIVE_PILL =
  "rounded-full border border-accent bg-accent-muted px-3 py-1 text-label text-accent";
const IDLE_PILL =
  "rounded-full border border-border bg-surface px-3 py-1 text-label text-text-secondary hover:border-border-hover";

export function SearchesToolbar({
  total,
  searchText,
  onSearchTextChange,
  owner,
  onOwnerChange,
  leads,
  status,
  onStatusChange,
}: SearchesToolbarProps) {
  // `owner=<id>` is reachable by link and survives a status change that drops
  // the lead from the options. A <select> whose value matches no option falls
  // back to the FIRST one, which would show "Mine" over somebody else's
  // searches — so an unlisted id gets an option of its own.
  const reserved = OWNER_OPTIONS.some((o) => o.value === owner);
  const listed = leads.some((l) => l.thrive_user_id === owner);
  const orphanOwner = !reserved && !listed ? owner : null;

  return (
    <div className="flex flex-col gap-3" data-testid="searches-toolbar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <h1 className="text-h1 text-text">My Searches</h1>
          <span className="text-label text-text-muted" data-testid="searches-count">
            ({total === null ? "—" : total.toLocaleString()})
          </span>
        </div>

        <select
          value={owner}
          onChange={(e) => onOwnerChange(e.target.value)}
          aria-label="Filter by owner"
          data-testid="searches-owner-select"
          className="rounded-md border border-border bg-surface px-3 py-1.5 text-body text-text hover:border-border-hover focus:border-border-hover focus:outline-none"
        >
          {OWNER_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
          {orphanOwner ? (
            <option value={orphanOwner}>Thrive user {orphanOwner}</option>
          ) : null}
          {leads.length > 0 ? (
            <optgroup label="Leads">
              {leads.map((lead) => (
                <option key={lead.thrive_user_id} value={lead.thrive_user_id}>
                  {leadLabel(lead)}
                </option>
              ))}
            </optgroup>
          ) : null}
        </select>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-60">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
            aria-hidden="true"
          />
          <input
            type="search"
            value={searchText}
            onChange={(e) => onSearchTextChange(e.target.value)}
            placeholder="Search by ID, name or client"
            aria-label="Search by ID, name or client"
            data-testid="searches-search"
            className="w-full rounded-md border border-border bg-surface py-1.5 pl-9 pr-3 text-body text-text placeholder:text-text-muted hover:border-border-hover focus:border-border-hover focus:outline-none"
          />
        </div>

        <fieldset
          className="flex min-w-0 flex-wrap items-center gap-1.5 border-0 p-0 m-0"
          data-testid="searches-status-pills"
        >
          <legend className="sr-only">Filter by status</legend>
          {STATUS_PILLS.map((pill) => {
            const active = pill.value === status;
            return (
              <button
                key={pill.value}
                type="button"
                aria-pressed={active}
                data-status={pill.value}
                onClick={() => onStatusChange(pill.value)}
                className={active ? ACTIVE_PILL : IDLE_PILL}
              >
                {pill.label}
              </button>
            );
          })}
        </fieldset>
      </div>
    </div>
  );
}
